"use client"

import { Download, Edit2, FileText, Save, X } from "lucide-react";
import { useEffect, useState } from "react";

export default function FormsAndDocuments() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [editedTitle, setEditedTitle] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);

  // Fetch documents
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const url = "/api/documents";
        
        const response = await fetch(url);
        const data = await response.json();

        if (data.success) {
          setDocuments(data.data);
        }
      } catch (error) {
        console.error("Error fetching documents:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  // Handle document selection
  const handleSelectDocument = (document) => {
    setSelectedDocument(document);
    setEditedTitle(document.title);
    setEditedContent(document.content);
    setIsEditing(false);
    setIsFullScreen(true);
  };

  // Handle edit mode
  const handleEditToggle = () => {
    if (isEditing) {
      setEditedContent(selectedDocument.content);
      setEditedTitle(selectedDocument.title);
    }
    setIsEditing(!isEditing);
  };

  // Handle save
  const handleSave = async () => {
    try {
      setSaveLoading(true);
      const response = await fetch(`/api/documents/${selectedDocument._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: editedTitle,
          description: selectedDocument.description,
          category: selectedDocument.category,
          content: editedContent,
          fileUrl: selectedDocument.fileUrl,
          fileName: selectedDocument.fileName,
          fileType: selectedDocument.fileType,
          editable: selectedDocument.editable,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSelectedDocument(data.data);
        setIsEditing(false);
        // Update the document in the list
        setDocuments(
          documents.map((doc) => (doc._id === data.data._id ? data.data : doc))
        );
      }
    } catch (error) {
      console.error("Error saving document:", error);
    } finally {
      setSaveLoading(false);
    }
  };

  // Handle download
  const handleDownload = (doc) => {
    if (doc.fileUrl) {
      // If there's a file URL, download from there
      const a = window.document.createElement("a");
      a.href = doc.fileUrl;
      a.download = doc.fileName || "document";
      a.click();
    } else if (doc.content) {
      // If there's content but no file, create a text file
      const element = window.document.createElement("a");
      const file = new Blob([doc.content], { type: "text/plain" });
      element.href = URL.createObjectURL(file);
      element.download = `${doc.title.replace(/\s+/g, "-").toLowerCase()}.txt`;
      element.click();
    }
  };

  return (
    <section className="py-20 bg-gradient-to-b from-blue-50 to-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Forms &amp; <span className="text-blue-500">Documents</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Download, edit, and manage all your important forms and documents in one place.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Documents List */}
          <div className="lg:col-span-2">
            <div className="mb-6 flex flex-wrap gap-2">
              <button className="px-4 py-2 rounded-full text-sm font-medium bg-blue-500 text-white">
                Forms &amp; Documents
              </button>
            </div>

            {/* Documents List */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
                  <p className="mt-2 text-gray-600">Loading documents...</p>
                </div>
              ) : documents.length === 0 ? (
                <div className="p-8 text-center">
                  <FileText className="mx-auto h-12 w-12 text-gray-300 mb-2" />
                  <p className="text-gray-500">No documents available</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {documents.map((doc) => (
                    <div
                      key={doc._id}
                      onClick={() => handleSelectDocument(doc)}
                      className={`p-4 cursor-pointer transition-all ${
                        selectedDocument?._id === doc._id
                          ? "bg-blue-50 border-l-4 border-blue-500"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">
                            {doc.title}
                          </h3>
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {doc.description || "No description"}
                          </p>
                          <div className="mt-2 flex items-center gap-2">
                            <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                              {doc.category}
                            </span>
                          </div>
                        </div>
                        <FileText className="h-6 w-6 text-blue-500 ml-2 flex-shrink-0" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Document Preview */}
          <div className="lg:col-span-2">
            {selectedDocument ? (
              <div className="bg-white rounded-xl shadow-md p-6 h-full flex flex-col">
                {/* Header */}
                <div className="flex items-start justify-between mb-4 pb-4 border-b">
                  <div className="flex-1">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e.target.value)}
                        className="text-2xl font-bold text-gray-900 w-full mb-2 px-2 py-1 border border-blue-300 rounded"
                      />
                    ) : (
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        {selectedDocument.title}
                      </h3>
                    )}
                    <p className="text-sm text-gray-600">
                      {selectedDocument.description}
                    </p>
                  </div>
                  <button
                    onClick={() => setIsFullScreen(true)}
                    className="text-sm px-3 py-1 rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200"
                  >
                    Open Full Screen
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto mb-4">
                  {selectedDocument.fileUrl ? (
                    <iframe
                      src={selectedDocument.fileUrl}
                      title={selectedDocument.title}
                      className="w-full h-64 rounded-lg border"
                    />
                  ) : (
                    <div className="prose prose-sm max-w-none">
                      {selectedDocument.content ? (
                        <p className="whitespace-pre-wrap text-gray-700">
                          {selectedDocument.content}
                        </p>
                      ) : (
                        <p className="text-gray-500 italic">
                          No content available for this document.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-4 border-t">
                  <button
                    onClick={() => handleDownload(selectedDocument)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-md p-8 h-full flex items-center justify-center">
                <div className="text-center">
                  <FileText className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                  <p className="text-gray-500 text-lg">
                    Select a document to preview in full screen
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {isFullScreen && selectedDocument && (
        <div className="fixed inset-0 z-[100] bg-white p-4 md:p-6">
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between border-b pb-3 mb-4">
              <div>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    className="text-xl md:text-2xl font-bold text-gray-900 w-full px-2 py-1 border border-blue-300 rounded"
                  />
                ) : (
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900">{selectedDocument.title}</h3>
                )}
              </div>
              <div className="flex items-center gap-2">
                {selectedDocument.editable && !isEditing && (
                  <button
                    onClick={handleEditToggle}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
                  >
                    <Edit2 className="h-4 w-4" />
                    Edit
                  </button>
                )}
                {selectedDocument.editable && isEditing && (
                  <>
                    <button
                      onClick={handleSave}
                      disabled={saveLoading}
                      className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium disabled:opacity-50"
                    >
                      <Save className="h-4 w-4" />
                      {saveLoading ? "Saving..." : "Save"}
                    </button>
                    <button
                      onClick={handleEditToggle}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </>
                )}
                <button
                  onClick={() => handleDownload(selectedDocument)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  <Download className="h-4 w-4" />
                  Download
                </button>
                <button
                  onClick={() => {
                    setIsFullScreen(false);
                    setIsEditing(false);
                  }}
                  className="p-2 text-gray-500 hover:text-gray-700"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="flex-1">
              {isEditing ? (
                <textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  className="w-full h-full p-4 border border-blue-300 rounded-lg font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter document content..."
                />
              ) : selectedDocument.fileUrl ? (
                <iframe
                  src={selectedDocument.fileUrl}
                  title={selectedDocument.title}
                  className="w-full h-full rounded-lg border"
                />
              ) : (
                <div className="h-full overflow-auto border rounded-lg p-4">
                  {selectedDocument.content ? (
                    <p className="whitespace-pre-wrap text-gray-700">{selectedDocument.content}</p>
                  ) : (
                    <p className="text-gray-500 italic">No content available for this document.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
